import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { ThemeContext } from '../ThemeContext';
import api from '../api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, History, TrendingUp, Play, Users, BookOpen, Sparkles, Star, Moon, Sun } from 'lucide-react';
import GameYourSatLogo from '../img/GameYourSatLogo.png';



// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  // Hover colors: Light mode: #06bae4, Dark mode: #ff9e42
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
          borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
    {children}
    </motion.div>
  );
};

const CardHeader = ({ title, link, icon: Icon }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);

  return (
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center gap-3">
        {Icon && (
          <motion.div
            animate={{ rotate: isHovered ? 360 : 0 }}
            transition={{ duration: 0.6 }}
            className="p-2 rounded-xl bg-[#a6a6a6] text-white"
          >
            <Icon size={20} />
          </motion.div>
        )}
        <motion.h3
          key={`${title}-${isDarkMode}`}
          className="font-bold text-2xl font-zonaProUltra relative inline-block"
          style={{
            color: isDarkMode ? '#ff7a00' : '#212D82',
            background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'shine 10s linear infinite',
            lineHeight: '1.2',
            paddingBottom: '0.25rem'
          }}
        >
          {title}
        </motion.h3>
      </div>
    {link && (
        <motion.div
          whileHover={{ scale: 1.2, rotate: -45 }}
          whileTap={{ scale: 0.9 }}
        >
          <Link
            to={link}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
        <FiChevronRight size={24} />
      </Link>
        </motion.div>
    )}
  </div>
);
};

const LeaderboardCard = () => {
    const [leaderboard, setLeaderboard] = useState([]);

    useEffect(() => {
        api.get('/api/leaderboard/?type=total&category=total_score')
            .then(response => {
                const data = response.data.topPlayers ?? [];
                setLeaderboard(data.slice(0, 3));
            })
            .catch(error => console.error("Error fetching leaderboard:", error));
    }, []);

    const rankDetails = [
        { medal: '🥇', color: 'text-yellow-400', glow: 'shadow-yellow-400/50' },
        { medal: '🥈', color: 'text-gray-400', glow: 'shadow-gray-400/50' },
        { medal: '🥉', color: 'text-amber-600', glow: 'shadow-amber-600/50' }
    ];

    return (
        <Card className="flex flex-col grow-[3]" delay={0.2} index={1}>
            <CardHeader title="Hall of Fame" link="/leaderboard" icon={Trophy} />
            <div className="flex-grow flex items-center space-x-4">
                <div className="block lg:hidden w-full flex justify-center items-center">
                    <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        whileTap={{ scale: 0.95 }}
                    >
                    <Link to="/leaderboard">
                        <img src="/img/leaderboard.png" alt="Leaderboard" className="w-36 h-36" draggable={false}/>
                    </Link>
                    </motion.div>
                </div>
                <div className="hidden lg:flex flex-grow flex items-center space-x-4 w-full">
                    <motion.div
                        className="flex-shrink-0"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <Link to="/leaderboard">    
                            <img src="/img/leaderboard.png" alt="Leaderboard" className="w-36 h-36 drop-shadow-2xl" draggable={false}/>
                        </Link>
                    </motion.div>
                    <div className="w-full pr-4">
                        <div className="grid grid-cols-3 gap-1 items-center text-xs font-bold text-gray-400 uppercase pb-3 border-b border-gray-200 dark:border-gray-700">
                            <span className="font-zonaProRegular text-sm">Rank</span>
                            <span className="font-zonaProRegular text-sm">Name</span>
                            <span className="text-right font-zonaProRegular text-sm">Score</span>
                        </div>
                        <div className="space-y-3 pt-3">
                            <AnimatePresence>
                            {leaderboard.map((player, index) => (
                                    <motion.div
                                        key={player.user_id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        transition={{ delay: index * 0.1, duration: 0.4 }}
                                        whileHover={{ x: 5, scale: 1.02 }}
                                        className="grid grid-cols-3 gap-1 items-center p-2 rounded-lg transition-all group"
                                        onMouseEnter={(e) => {
                                            const isDark = document.documentElement.classList.contains('dark');
                                            e.currentTarget.style.backgroundColor = isDark ? 'rgba(6, 186, 228, 0.15)' : 'rgba(6, 186, 228, 0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                            
                                        {rankDetails[index]?.medal}
                                        <span className="font-semibold text-gray-800 dark:text-gray-200 truncate font-zonaProRegular text-sm">{player.user_name}</span>
                                        <motion.span
                                            className="font-bold text-right font-zonaProRegular text-sm"
                                            style={{ color: '#ff9e42' }}
                                            initial={{ opacity: 0 }}
                                            animate={{ 
                                                opacity: 1,
                                                scale: [1, 1.1, 1],
                                            }}
                                            transition={{ 
                                                opacity: { delay: index * 0.1 + 0.3 },
                                                scale: {
                                                    duration: 1,
                                                    repeat: Infinity,
                                                    delay: index * 0.2,
                                                }
                                            }}
                                        >
                                            {player.score.toLocaleString()}
                                        </motion.span>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};


const JourneyCard = ({ isPremium, trialUsed }) => {
    const { isDarkMode } = useContext(ThemeContext);
    const gameModes = [
        { path: "/solo", icon: Play, label: "Solo", color: "from-cyan-400 to-blue-500", premium: true },
        { path: "/compete", icon: Users, label: "Compete", color: "from-purple-400 to-pink-500", premium: true },
        { path: "/practice", icon: BookOpen, label: "Practice", color: "from-lime-400 to-green-500", premium: true },
    ];

    return (
        <Card className="flex flex-col h-full relative" delay={0} index={0}>
            <CardHeader title="Begin Challenge" icon={Sparkles} />
            <div className="flex-grow flex flex-col items-center justify-evenly gap-4">
            {isPremium ? (
                <>
                        {gameModes.map((mode, index) => (
                            <motion.div
                                key={mode.path}
                                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ delay: index * 0.15, duration: 0.5, type: "spring" }}
                                whileHover={{ scale: 1.1, y: -5 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Link
                                    to={mode.path}
                                    className="group relative flex flex-col items-center justify-center p-4 rounded-2xl bg-gradient-to-br from-white/50 to-white/30 dark:from-gray-800/50 dark:to-gray-700/30 backdrop-blur-sm border border-white/20 dark:border-gray-600/20 w-full"
                                >
                                    <motion.div
                                        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"
                                        style={{ backgroundColor: '#ff9e42' }}
                                    />
                                    <motion.img
                                        src={`/img/${mode.label.toLowerCase()}.png`}
                                        alt={mode.label}
                                        className="w-48 h-48 object-contain relative z-10 drop-shadow-lg"
                                        draggable={false}
                                        whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                                        transition={{ duration: 0.5 }}
                                    />
                    </Link>
                            </motion.div>
                        ))}
                </>
            ) : (
                <>
                        <div className="relative w-full flex flex-col items-center justify-center gap-4">
                            {gameModes.map((mode, index) => (
                                <motion.div
                                    key={mode.path}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: mode.premium ? 0.5 : 1, y: 0 }}
                                    transition={{ delay: index * 0.1, duration: 0.4 }}
                                    className={mode.premium ? "relative" : ""}
                                >
                                    <Link
                                        to={mode.premium ? "#" : mode.path}
                                        className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all ${
                                            mode.premium
                                                ? "cursor-not-allowed opacity-50"
                                                : "hover:bg-gradient-to-br hover:from-lime-50 hover:to-green-50 dark:hover:from-gray-700 dark:hover:to-gray-600"
                                        }`}
                                    >
                                        <motion.img
                                            src={`/img/${mode.label.toLowerCase()}.png`}
                                            alt={mode.label}
                                            className="w-48 h-48 object-contain"
                                            draggable={false}
                                            whileHover={!mode.premium ? { scale: 1.15, rotate: 5 } : {}}
                                        />
                            </Link>
                                </motion.div>
                            ))}
                    </div>
                </>
            )}
        </div>
            {!isPremium && (
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 z-40 flex items-center justify-center bg-white/30 dark:bg-gray-900/30 backdrop-blur-none rounded-3xl"
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            className="bg-gradient-to-br from-white/95 to-white/90 dark:from-gray-800/95 dark:to-gray-700/90 rounded-3xl p-6 text-center shadow-2xl border border-white/20 dark:border-gray-600/20"
                            whileHover={{ scale: 1.05 }}
                        >
                            <motion.div
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="inline-block mb-3"
                            >
                                <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                            </motion.div>
                            <p className="text-gray-700 dark:text-gray-200 mb-4 font-zonaProRegular text-sm">
                                Unlock Solo and Compete with Subscription
                            </p>
                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                <Link
                                    to="/subscription"
                                    className="inline-block py-3 px-6 bg-[#34c759] text-white text-base font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                                >
                                    {trialUsed !== true ? 'Start Free Trial' : 'Subscribe'}
                                </Link>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </AnimatePresence>
            )}
    </Card>
    );
};

const AnswerHistoryCard = ({ history }) => {
    const getStatusText = (item) => {
        if (item.question_status === 'unanswered') return "Unanswered";
        return item.question_status === 'correct' ? "Correct" : "Incorrect";
    };

    const getStatusColor = (item) => {
        if (item.question_status === 'unanswered') return 'text-gray-500';
        return item.question_status === 'correct' ? 'text-green-500' : 'text-red-500';
    };

    return (
        <Card className="flex flex-col grow-[3]" delay={0.4} index={2}>
            <CardHeader title="History Log" link="/answer-history" icon={History} />
        <div className="flex-grow flex items-center space-x-4">
            <div className="block lg:hidden w-full flex justify-center items-center">
                    <motion.div whileHover={{ scale: 1.1, rotate: 5 }}>
                <Link to="/answer-history">
                            <img src="/img/answer_history.png" alt="Answer History" className="w-36 h-36 drop-shadow-2xl" draggable={false}/>
                </Link>    
                    </motion.div>
            </div>
            <div className="hidden lg:flex flex-grow flex items-center space-x-4 w-full">
                    <motion.div
                        className="flex-shrink-0"
                        whileHover={{ scale: 1.1, rotate: -5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                    <Link to="/answer-history">
                            <img src="/img/answer_history.png" alt="Answer History" className="w-36 h-36 drop-shadow-2xl" draggable={false}/>
                    </Link>
                    </motion.div>
                {history && history.length > 0 ? (
                    <div className="w-full pr-4">
                            <div className="grid grid-cols-5 gap-2 items-center text-xs font-bold text-gray-400 uppercase pb-3 border-b border-gray-200 dark:border-gray-700">
                            <span className="col-span-2 font-zonaProRegular text-sm">Subject</span>
                            <span className="font-zonaProRegular text-sm">Status</span>
                            <span className="text-right font-zonaProRegular text-sm">Bet</span>
                            <span className="text-right font-zonaProRegular text-sm">Points</span>
                        </div>
                            <div className="space-y-3 pt-3">
                                <AnimatePresence>
                                    {history.slice(0, 3).map((item, index) => (
                                        <motion.div
                                            key={item.id || index}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            transition={{ delay: index * 0.1, duration: 0.4 }}
                                            whileHover={{ x: 5, scale: 1.02 }}
                                            className="grid grid-cols-5 gap-2 items-center text-sm p-2 rounded-lg transition-all"
                                            onMouseEnter={(e) => {
                                                const isDark = document.documentElement.classList.contains('dark');
                                                e.currentTarget.style.backgroundColor = isDark ? 'rgba(6, 186, 228, 0.15)' : 'rgba(6, 186, 228, 0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <span className="font-semibold text-gray-800 dark:text-gray-200 truncate col-span-2 font-zonaProRegular text-sm">{item.content_name}</span>
                                            <motion.span
                                                className={`font-bold ${getStatusColor(item)} font-zonaProRegular text-sm`}
                                                whileHover={{ scale: 1.2 }}
                                            >
                                                {getStatusText(item)}
                                            </motion.span>
                                            <span className="font-semibold text-gray-800 dark:text-gray-200 text-right font-zonaProRegular text-sm">{item.bet_amt?.toLocaleString() || 0}</span>
                                            <motion.span
                                                className="font-bold text-right font-zonaProRegular text-sm"
                                                style={{ color: '#ff9e42' }}
                                                animate={{
                                                    scale: [1, 1.1, 1],
                                                }}
                                                transition={{
                                                    duration: 1,
                                                    repeat: Infinity,
                                                    delay: index * 0.2,
                                                }}
                                            >
                                        {item.points >= 0 ? `+${item.points}` : item.points}
                                            </motion.span>
                                        </motion.div>
                            ))}
                                </AnimatePresence>
                        </div>
                    </div>
                ) : (
                        <motion.div
                            className="w-full flex items-center justify-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <p className="text-gray-500 dark:text-gray-400 font-semibold">No Questions Attempted Yet!</p>
                        </motion.div>
                )}
            </div>
        </div>
    </Card>
    );
};


const StatsCard = ({ history }) => {
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        if (!history || history.length === 0) {
            setChartData([]);
            return;
        }

        const dailyData = history.reduce((acc, ans) => {
          if (!ans.created_date) return acc;
          const date = new Date(ans.created_date).toISOString().split('T')[0];
          if (!acc[date]) {
            acc[date] = { date, correct: 0, incorrect: 0 };
          }
          if (ans.question_status === 'correct') acc[date].correct++;
          else if (ans.question_status === 'incorrect') acc[date].incorrect++;
          return acc;
        }, {});

        const dailyAccuracyTrend = Object.values(dailyData)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .map(day => {
            const totalAttempted = day.correct + day.incorrect;
            const accuracy = totalAttempted > 0 ? (day.correct / totalAttempted) * 100 : 0;
            return { date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), accuracy };
          })
          .slice(-15);
        setChartData(dailyAccuracyTrend);
    }, [history]);

    return (
        <Card className="flex flex-col grow-[0.5]" delay={0.6} index={3}>
            <CardHeader title="Track Record" link="/analytics" icon={TrendingUp} />
        <div className="flex-grow flex items-center space-x-4">
            <div className="block lg:hidden w-full flex justify-center items-center">
                    <motion.div whileHover={{ scale: 1.1, rotate: -5 }}>
                <Link to="/analytics">
                            <img src="/img/analytics.png" alt="Stats" className="w-36 h-36 drop-shadow-2xl" draggable={false}/>
                </Link>
                    </motion.div>
            </div>
            <div className="hidden lg:flex flex-grow flex items-center space-x-4 w-full">
                    <motion.div
                        className="flex-shrink-0"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                    <Link to="/analytics">
                            <img src="/img/analytics.png" alt="Stats" className="w-36 h-36 drop-shadow-2xl" draggable={false}/>
                    </Link>
                    </motion.div>
                {history && history.length > 0 ? (
                        <motion.div
                            className="w-full flex flex-col"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <h4 className="text-center font-semibold text-gray-500 dark:text-gray-400 mb-2 font-zonaProRegular text-sm">Daily Performance</h4>
                        <div className="h-36">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={10} angle={-20} textAnchor="end" height={40} fontFamily="Zona Pro Regular" />
                                    <YAxis unit="%" domain={[0, 100]} stroke="#9ca3af" fontSize={12} fontFamily="Zona Pro Regular" />
                                    <Tooltip
                                        formatter={(value) => `${value.toFixed(1)}%`}
                                            wrapperStyle={{ fontSize: '12px', fontFamily: 'Zona Pro Regular', background: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', padding: '8px' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="accuracy"
                                            stroke="url(#gradient)"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: '#ff9e42' }}
                                            activeDot={{ r: 8, fill: '#ff9e42' }}
                                        />
                                        <defs>
                                            <linearGradient id="gradient" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#ff9e42" />
                                                <stop offset="100%" stopColor="#06bae4" />
                                            </linearGradient>
                                        </defs>
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            className="w-full flex items-center justify-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <p className="text-gray-500 dark:text-gray-400 font-semibold">No Questions Attempted Yet!</p>
                        </motion.div>
                )}
            </div>
        </div>
    </Card>
    );
};

const GameOptionsPage = () => {
    const [answerHistory, setAnswerHistory] = useState([]);
    const { user } = useContext(UserContext);
    let storedUser = null;
    try {
        const raw = localStorage.getItem('sat_user');
        storedUser = raw ? JSON.parse(raw) : null;
    } catch {
        storedUser = null;
    }
    const userData = user || storedUser;
    const isPremium = userData?.is_premium || false;
    const trialUsed = userData?.trial_used || false;
    const navigate = useNavigate();
    const { isDarkMode, toggleDarkMode } = useContext(ThemeContext);

    useEffect(() => {
        if (!user?.user_id) return;
        api.get(`/api/user/answer_history/`)
            .then(response => {
                setAnswerHistory(response.data.answer_history || []);
            })
            .catch(error => {
                console.error("Error fetching answer history:", error)
                setAnswerHistory([]);
            });
    }, [user?.user_id]);


  return (
        <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            {/* Main Content */}
            <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
                {/* Animated Header */}
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, type: "spring" }}
                    className="text-center mb-8 relative"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex justify-center mb-4"
                    >
                        <img 
                            src={GameYourSatLogo} 
                            alt="GameYourSAT Logo" 
                            className="h-16 sm:h-20 md:h-24 lg:h-32 w-auto object-contain"
                            draggable={false}
                        />
                    </motion.div>
                </motion.div>

                {/* Cards Grid */}
                <div className="w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
                    <JourneyCard isPremium={isPremium} trialUsed={trialUsed} />
                    <div className="lg:col-span-3 flex flex-col gap-4 lg:gap-6 relative">
                <LeaderboardCard />
                <AnswerHistoryCard history={answerHistory} />
                <StatsCard history={answerHistory} />
                {!isPremium && (
                            <AnimatePresence>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 z-40 flex items-center justify-center backdrop-blur-none bg-white/30 dark:bg-gray-900/30 rounded-3xl"
                                >
                                    <motion.div
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0.8, opacity: 0 }}
                                        className="backdrop-blur-xl bg-white/95 dark:bg-gray-800/95 rounded-3xl p-8 text-center shadow-2xl border border-white/20 dark:border-gray-700/50 max-w-md"
                                    >
                                        <motion.div
                                            animate={{ rotate: [0, 360] }}
                                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                            className="inline-block mb-4"
                                        >
                                            <Sparkles className="w-12 h-12 text-yellow-400" />
                                        </motion.div>
                                        <p className="text-gray-700 dark:text-gray-300 mb-6 font-zonaProRegular">
                                            {trialUsed !== true 
                                                ? 'Start your 7-day free trial to access Leaderboard, History, and Analytics.' 
                                                : 'Subscribe to the premium plan to access Leaderboard, History, and Analytics.'}
                                        </p>
                                        <motion.button
                                onClick={() => navigate('/subscription')}
                                            className="py-3 px-8 bg-[#34c759] text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                            >
                                {trialUsed !== true ? 'Start Free Trial' : 'Subscribe'}
                                        </motion.button>
                                    </motion.div>
                                </motion.div>
                            </AnimatePresence>
                        )}
                    </div>
                </div>
            </div>
        </div>
  );
};

export default GameOptionsPage;